import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  GROUP_ROLE_TEMPLATE_NONE,
  GROUP_ROLE_TEMPLATES
} from '../../constants/groupRoleTemplates';

/**
 * Compact permissions template: badge picker + info bubble (details popup).
 */
const GroupsRoleTemplateField = ({
  value = GROUP_ROLE_TEMPLATE_NONE,
  onChange,
  disabled = false,
  id = 'create-group-role-template'
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  const wrapRef = useRef(null);

  const selected = GROUP_ROLE_TEMPLATES.find((t) => t.id === value);
  const badgeLabel = selected?.label || 'None';

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [menuOpen]);

  const options = [
    { id: GROUP_ROLE_TEMPLATE_NONE, label: 'None', description: 'Configure permissions manually later.' },
    ...GROUP_ROLE_TEMPLATES.map((t) => ({
      id: t.id,
      label: t.label,
      description: t.description
    }))
  ];

  return (
    <div className="groups-perm-template" ref={wrapRef}>
      <div className="groups-perm-template-row">
        <span className="groups-perm-template-label" id={`${id}-label`}>
          Permissions template
          <button
            type="button"
            className="groups-info-bubble"
            aria-label="About permissions templates"
            title="About permissions templates"
            onClick={() => setInfoOpen(true)}
            disabled={disabled}
          >
            i
          </button>
        </span>
        <button
          type="button"
          id={id}
          className={`groups-perm-template-badge${menuOpen ? ' is-open' : ''}${
            value ? ' has-value' : ''
          }`}
          aria-haspopup="listbox"
          aria-expanded={menuOpen}
          aria-labelledby={`${id}-label`}
          onClick={() => !disabled && setMenuOpen((o) => !o)}
          disabled={disabled}
        >
          {badgeLabel}
          <span className="groups-perm-template-caret" aria-hidden>
            ▾
          </span>
        </button>
      </div>

      {menuOpen ? (
        <ul className="groups-perm-template-menu" role="listbox" aria-labelledby={`${id}-label`}>
          {options.map((opt) => (
            <li key={opt.id || 'none'} role="option" aria-selected={value === opt.id}>
              <button
                type="button"
                className={`groups-perm-template-option${value === opt.id ? ' is-selected' : ''}`}
                onClick={() => {
                  onChange?.(opt.id);
                  setMenuOpen(false);
                }}
              >
                <strong>{opt.label}</strong>
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      {infoOpen
        ? createPortal(
            <div
              className="profile-sheet-overlay profile-sheet-overlay--raised"
              role="presentation"
              onClick={() => setInfoOpen(false)}
            >
              <div
                className="profile-sheet-panel profile-sheet-panel--form groups-perm-template-info-panel"
                role="dialog"
                aria-modal="true"
                aria-labelledby={`${id}-info-title`}
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
              >
                <div className="profile-sheet-header">
                  <div className="profile-sheet-header-main">
                    <h2 id={`${id}-info-title`} className="profile-sheet-title">
                      Permissions templates
                    </h2>
                    <p className="profile-sheet-subtext">
                      Applies default page permissions to the new group-period after create. Choose
                      None to configure manually in Page Permissions Manager.
                    </p>
                  </div>
                  <button
                    type="button"
                    className="profile-sheet-close"
                    onClick={() => setInfoOpen(false)}
                    aria-label="Close"
                  >
                    ×
                  </button>
                </div>
                <div className="profile-sheet-form-body">
                  <ul className="groups-perm-template-info-list">
                    {options.map((opt) => (
                      <li key={opt.id || 'none'}>
                        <strong>{opt.label}</strong>
                        <p>{opt.description}</p>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="profile-sheet-actions">
                  <button
                    type="button"
                    className="profile-sheet-btn profile-sheet-btn--primary"
                    onClick={() => setInfoOpen(false)}
                  >
                    Got it
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )
        : null}
    </div>
  );
};

export default GroupsRoleTemplateField;
